import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { getDocumentName } from '@e-socna/data-layer';

import { FolderPage_OneFolder_QueryDocument } from '../../../pages/Folder/hooks/useFindOneFolderQuery.hook';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { GatedButton } from '../../Access/GatedButton';
import type { Notice } from '../../NoticesAlert';

import type { ParentActorsLevel } from './useParentActorsLevel.hook';

interface Params {
  readonly isInternal: boolean;
  readonly parent: ParentActorsLevel;
}

// The queries holding the actors of the parent levels, reloaded once the user is back from
// editing them in another tab.
const PARENT_QUERY_KEYS = [
  [getDocumentName(FolderPage_OneFolder_QueryDocument)],
  [getDocumentName(MissionPage_OneMission_QueryDocument)],
] as const;

// The actors of a list are chosen among the same list of the parent level: tells it, and opens
// that list in a new tab to complete it.
export const useParentActorsNotice = ({
  isInternal,
  parent,
}: Readonly<Params>): Notice => {
  const queryClient = useQueryClient();
  const isEditingParentRef = useRef(false);
  const listLabel = isInternal ? 'internes' : 'externes';

  useEffect(() => {
    const reloadParent = () => {
      if (!isEditingParentRef.current) {
        return;
      }

      // eslint-disable-next-line functional/immutable-data
      isEditingParentRef.current = false;
      PARENT_QUERY_KEYS.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    };

    window.addEventListener('focus', reloadParent);

    return () => {
      window.removeEventListener('focus', reloadParent);
    };
  }, [queryClient]);

  const editPath = parent.editPathOf(isInternal);
  const editParent = useCallback(() => {
    // eslint-disable-next-line functional/immutable-data
    isEditingParentRef.current = true;
    window.open(editPath, '_blank', 'noopener');
  }, [editPath]);

  return {
    message: `Les acteurs ${listLabel} se choisissent parmi ceux ${parent.ofLabel}. Pour en ajouter un autre, ajoutez-le d'abord aux acteurs ${listLabel} ${parent.ofLabel}.`,
    action: (
      <GatedButton
        color="inherit"
        endIcon={<OpenInNewIcon />}
        gate={parent.editGate}
        onClick={editParent}
        size="small"
      >
        {`Modifier les acteurs ${listLabel} ${parent.ofLabel}`}
      </GatedButton>
    ),
  };
};
